import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Grid, 
  Button, 
  CircularProgress, 
  Alert, 
  Box,
  Typography 
} from '@mui/material';
import { post } from 'src/services/httpClient.service';

const API_URL = process.env.REACT_APP_BASE_API_URL;

interface AddPermissionProps {
  handleClose: () => void;
  onSubmit?: () => void;
  fetchPermissionsData?: () => void;
}

const AddPermission: React.FC<AddPermissionProps> = ({ 
  handleClose, 
  onSubmit, 
  fetchPermissionsData 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prefix: '',
    sequence: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value as string,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        ...formData,
        sequence: parseInt(formData.sequence) || 0
      };

      const response = await post(`${API_URL}/api/permissions`, payload);
      
      if (response.data) {
        setSuccess('Permission created successfully!');
        setFormData({ 
          name: '', 
          description: '', 
          prefix: '', 
          sequence: '' 
        });
        
        if (onSubmit) onSubmit();
        if (fetchPermissionsData) fetchPermissionsData();
        handleClose();
      }
    } catch (err: any) {
      console.error('Error creating permission:', err);
      const errorMessage = err.response?.data?.message || 'Failed to create permission';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({ 
      name: '', 
      description: '', 
      prefix: '', 
      sequence: '' 
    });
    setError('');
    setSuccess('');
    handleClose();
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      {/* <Typography variant="h6" sx={{ mb: 3 }}>
        Add New Permission
      </Typography> */}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Permission Name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              placeholder="Enter permission name"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              placeholder="Enter permission description"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="prefix"
              label="Prefix"
              value={formData.prefix}
              onChange={handleChange}
              fullWidth
              required
              placeholder="e.g., CREATE, READ, UPDATE, DELETE"
              helperText="Short code for the permission"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="sequence"
              label="Sequence"
              type="number"
              value={formData.sequence}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ min: 0 }}
              helperText="Display order sequence"
            />
          </Grid>

          <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleCancel} 
              disabled={loading} 
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Permission'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AddPermission;